import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from 'src/app/core/services/alert.service';

/**
 * Client-side payload safety net (CWE-20).
 *
 * Recursively scans every outgoing JSON request body and blocks the request when a string field
 * contains script-like content or is absurdly long. This is a blanket, app-wide defense layer —
 * per-form validators still handle fine-grained, field-level rules with inline error messages.
 *
 * NOTE: this is defense-in-depth only. The authoritative fix is server-side validation.
 */

/** Any single string longer than this is rejected (except known binary/large fields below). */
const MAX_STRING_LENGTH = 5000;

/** Script/HTML-injection sequences rejected on EVERY string field, including rich text. */
const SCRIPT_LIKE = /(<\s*script)|(<\s*\/\s*script)|(javascript:)|(\son\w+\s*=)/i;

/**
 * Field-name fragments whose values may legitimately be very long (base64 blobs, rich text, etc.)
 * — these are exempt from the length check, but the script-like check still applies.
 */
const LENGTH_EXEMPT_KEY_FRAGMENTS = [
  'image', 'photo', 'file', 'document', 'signature', 'logo', 'attachment',
  'base64', 'qr', 'content', 'description', 'body', 'html', 'agreement',
];

interface Violation {
  field: string;
  reason: 'unsafe' | 'too_long';
}

function isLengthExempt(key: string, value: string): boolean {
  if (value.startsWith('data:')) return true; // data URLs
  const lower = key.toLowerCase();
  return LENGTH_EXEMPT_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment));
}

function collectViolations(value: unknown, path: string, out: Violation[]): void {
  if (typeof value === 'string') {
    const field = path.split('.').pop() || path || 'value';
    if (SCRIPT_LIKE.test(value)) {
      out.push({ field, reason: 'unsafe' });
    } else if (value.length > MAX_STRING_LENGTH && !isLengthExempt(field, value)) {
      out.push({ field, reason: 'too_long' });
    }
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => collectViolations(item, `${path}[${index}]`, out));
  } else if (value && typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, val]) =>
      collectViolations(val, path ? `${path}.${key}` : key, out)
    );
  }
}

export const payloadValidationInterceptor: HttpInterceptorFn = (req, next) => {
  const body = req.body;
  const isMutating = req.method === 'POST' || req.method === 'PUT' || req.method === 'PATCH';
  const isPlainObject =
    !!body &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer);

  if (!isMutating || !isPlainObject) {
    return next(req);
  }

  const violations: Violation[] = [];
  collectViolations(body, '', violations);

  if (violations.length === 0) {
    return next(req);
  }

  const alertService = inject(AlertService);
  const translate = inject(TranslateService);

  // De-duplicate per field/reason so the toast stays short.
  const lines = Array.from(
    new Set(
      violations.map(
        (v) =>
          `${v.field}: ${translate.instant(
            v.reason === 'unsafe'
              ? 'validation.unsafe_chars.capitalize'
              : 'validation.maxlength.capitalize'
          )}`
      )
    )
  );

  alertService.alert({
    type: 'error',
    message: `${translate.instant('invalid_input_blocked.capitalize')}\n${lines.join('\n')}`,
  });

  // Block the request before it leaves the browser.
  return throwError(
    () =>
      new HttpErrorResponse({
        status: 0,
        statusText: 'Blocked by client-side payload validation',
        url: req.url,
      })
  );
};
